/**
 * Page Rapports — Retirées (Phase 101db, Feb 2026).
 *
 * Rapport centralisé des barquettes retirées (DLC, casse, refill éclair) par
 * période / distributeur, avec agrégats et export CSV pour audit + envoi
 * comptable.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, Download, PackageMinus, AlertCircle, ExternalLink } from "lucide-react";
import { localInputToIso, isoToLocalInput } from "../lib/format";
import DlcLossesWeekly from "../components/reports/DlcLossesWeekly";
import ReassortGaps from "../components/reports/ReassortGaps";

const fmtDate = (iso) => (iso || "").slice(0, 19).replace("T", " ");
const fmtQty = (n) => Number(n || 0).toFixed(0);

function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfDay() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

export default function ReportsRetirees() {
  const [distributors, setDistributors] = useState([]);
  const [distributorId, setDistributorId] = useState("");
  const [dateFrom, setDateFrom] = useState(() => isoToLocalInput(firstOfMonth().toISOString()));
  const [dateTo, setDateTo] = useState(() => isoToLocalInput(endOfDay().toISOString()));
  const [view, setView] = useState("pertes"); // "pertes" | "retraits_distrib"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/distributors")
      .then((r) => setDistributors(r.data || []))
      .catch(() => setDistributors([]));
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (distributorId) params.set("distributor_id", distributorId);
      if (dateFrom) params.set("date_from", localInputToIso(dateFrom));
      if (dateTo) params.set("date_to", localInputToIso(dateTo));
      params.set("view", view);
      const { data } = await api.get(`/reports/retirees?${params.toString()}`);
      setData(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isWeekly = view === "dlc_weekly" || view === "reassort_gaps";
  useEffect(() => { if (!isWeekly) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [view]);

  const downloadCsv = () => {
    const params = new URLSearchParams();
    if (distributorId) params.set("distributor_id", distributorId);
    if (dateFrom) params.set("date_from", localInputToIso(dateFrom));
    if (dateTo) params.set("date_to", localInputToIso(dateTo));
    params.set("view", view);
    const url = `${api.defaults.baseURL}/reports/retirees.csv?${params.toString()}`;
    window.open(url, "_blank");
  };

  const rows = data?.rows || [];
  const byProduct = data?.by_product || [];
  const bySource = data?.by_source || [];
  const byDistributor = data?.by_distributor || [];
  const isPertes = view === "pertes";

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="reports-retirees-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-lg bg-[#A04E2C] flex items-center justify-center text-white">
          <PackageMinus className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-medium text-[#1A1C1A]">
            {view === "reassort_gaps" ? "Écarts de réassort" : view === "dlc_weekly" ? "Pertes DLC par semaine" : isPertes ? "Rapport « Pertes définitives »" : "Rapport « Retraits par distributeur »"}
          </h1>
          <p className="text-sm text-[#575A57]">
            {view === "reassort_gaps"
              ? "Barquettes qu'AgroManage croyait encore dans un emplacement alors que Nayax ne les comptait plus au réassort. Le plus gros écart en premier."
              : view === "dlc_weekly"
              ? "Par distributeur, plat et semaine, de la plus grosse perte à la plus petite, au coût de revient."
              : isPertes
              ? "Barquettes détruites physiquement (DLC dépassée au Stock Conthey, casse, blocage). Vérité comptable — utilisé pour audit + transmission comptable."
              : "Barquettes retirées de chaque distributeur (physiquement sorties de la machine, même si ré-injectées à Stock Conthey pour revente). Utilisé pour le commissionnement des partenaires."}
          </p>
        </div>
      </div>

      {/* Toggle vue */}
      <div className="flex gap-1 bg-[#F5F5F0] p-1 rounded-lg w-fit" data-testid="view-toggle">
        <button
          onClick={() => setView("pertes")}
          data-testid="view-pertes-btn"
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            isPertes ? "bg-white text-[#1A1C1A] shadow-sm" : "text-[#575A57] hover:text-[#1A1C1A]"
          }`}
        >
          📉 Pertes définitives (compta)
        </button>
        <button
          onClick={() => setView("retraits_distrib")}
          data-testid="view-retraits-btn"
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            view === "retraits_distrib" ? "bg-white text-[#1A1C1A] shadow-sm" : "text-[#575A57] hover:text-[#1A1C1A]"
          }`}
        >
          🔁 Retraits par distributeur (partenaires)
        </button>
        <button
          onClick={() => setView("dlc_weekly")}
          data-testid="view-dlc-weekly-btn"
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            view === "dlc_weekly" ? "bg-white text-[#1A1C1A] shadow-sm" : "text-[#575A57] hover:text-[#1A1C1A]"
          }`}
        >
          Pertes DLC par semaine
        </button>
        <button
          onClick={() => setView("reassort_gaps")}
          data-testid="view-reassort-gaps-btn"
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            view === "reassort_gaps" ? "bg-white text-[#1A1C1A] shadow-sm" : "text-[#575A57] hover:text-[#1A1C1A]"
          }`}
        >
          Écarts de réassort
        </button>
      </div>

      {view === "reassort_gaps" ? <ReassortGaps distributors={distributors} /> : view === "dlc_weekly" ? <DlcLossesWeekly distributors={distributors} /> : (<>

      {/* Filtres */}
      <div className="bg-white border border-[#E5E5E0] rounded-xl p-5 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="mb-1.5 block text-xs">Distributeur</Label>
            <Select value={distributorId || "__ALL__"} onValueChange={(v) => setDistributorId(v === "__ALL__" ? "" : v)}>
              <SelectTrigger data-testid="filter-distributor"><SelectValue placeholder="Tous" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">Tous</SelectItem>
                {distributors.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Du</Label>
            <Input type="datetime-local" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-from" />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Au</Label>
            <Input type="datetime-local" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-to" />
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={load} disabled={loading} className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white flex-1" data-testid="apply-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Appliquer"}
            </Button>
            <Button onClick={downloadCsv} variant="outline" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-[#FCEEEA] border border-[#C05C47]/30 text-[#8B3D2D] text-sm rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {/* KPIs + agrégats */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="report-summary">
          <Kpi
            label={isPertes ? "Total pertes définitives" : "Total retraits"}
            value={`${fmtQty(data.total_qty)} barquettes`}
            sub={`${data.total_rows} interventions`}
          />
          <div className="bg-white border border-[#E5E5E0] rounded-xl p-5 shadow-sm">
            <div className="text-[10px] uppercase tracking-wider text-[#575A57] mb-2">
              {isPertes ? "Par source" : "Par distributeur"}
            </div>
            <div className="space-y-1">
              {isPertes
                ? bySource.map((s) => (
                    <div key={s.source} className="flex justify-between text-sm">
                      <span>{s.source}</span>
                      <b className="font-mono">{fmtQty(s.quantity)}</b>
                    </div>
                  ))
                : byDistributor.slice(0, 8).map((d) => (
                    <div key={d.distributor_id} className="flex justify-between text-sm gap-2">
                      <span className="truncate flex-1">{d.distributor_name}</span>
                      <b className="font-mono">{fmtQty(d.quantity)}</b>
                    </div>
                  ))}
              {(isPertes ? bySource : byDistributor).length === 0 && (
                <div className="text-xs text-[#A3A6A3] italic">—</div>
              )}
            </div>
          </div>
          <div className="bg-white border border-[#E5E5E0] rounded-xl p-5 shadow-sm">
            <div className="text-[10px] uppercase tracking-wider text-[#575A57] mb-2">Top produits</div>
            <div className="space-y-1">
              {byProduct.slice(0, 5).map((p) => (
                <div key={p.product_id} className="flex justify-between text-sm gap-2">
                  <span className="truncate flex-1">{p.product_name || "?"}</span>
                  <b className="font-mono">{fmtQty(p.quantity)}</b>
                </div>
              ))}
              {byProduct.length === 0 && <div className="text-xs text-[#A3A6A3] italic">—</div>}
            </div>
          </div>
        </div>
      )}

      {/* Détail */}
      <div className="bg-white border border-[#E5E5E0] rounded-xl shadow-sm overflow-hidden" data-testid="report-detail">
        <div className="px-5 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
          <h2 className="font-heading text-base font-medium">
            {isPertes ? "Détail des pertes définitives" : "Détail des retraits par distributeur"}
          </h2>
          <span className="text-xs text-[#575A57]">{rows.length} lignes</span>
        </div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F0] sticky top-0">
              <tr className="text-xs text-[#575A57]">
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">Distributeur</th>
                <th className="text-left px-3 py-2 font-medium">Produit</th>
                <th className="text-left px-3 py-2 font-medium">Lot</th>
                <th className="text-right px-3 py-2 font-medium">Qté</th>
                <th className="text-left px-3 py-2 font-medium">Motif</th>
                <th className="text-left px-3 py-2 font-medium">Source</th>
                <th className="text-left px-3 py-2 font-medium">Opérateur</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[#F0F0EB] hover:bg-[#FAFAF7]">
                  <td className="px-3 py-1.5 whitespace-nowrap text-[#575A57] font-mono text-xs">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-1.5">{r.distributor_name}</td>
                  <td className="px-3 py-1.5">
                    <div className="font-medium">{r.product_name}</div>
                    {r.product_sku && <div className="text-[10px] text-[#A3A6A3]">{r.product_sku}</div>}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.lot_number || "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtQty(r.quantity)}</td>
                  <td className="px-3 py-1.5 text-xs">{r.reason}</td>
                  <td className="px-3 py-1.5">
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold bg-[#FBEEEA] text-[#A04E2C] border-[#E8B0A0]">
                      {r.source}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-xs">{r.operator || <span className="italic text-[#A3A6A3]">—</span>}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-sm text-[#A3A6A3] italic">
                    Aucune retirée sur cette période.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>)}
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-xl p-5 shadow-sm">
      <div className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1">{label}</div>
      <div className="text-2xl font-mono font-semibold text-[#A04E2C]">{value}</div>
      {sub && <div className="text-xs text-[#575A57] mt-0.5">{sub}</div>}
    </div>
  );
}
